import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faUser, faEnvelope, faPhone, faMapMarkerAlt, faEdit, faCheck, faCalendar, faChartLine, faHeart, faEye, faChevronRight, faShieldAlt, faBell, faGlobe, faMoon, faTag, faStar, faBookmark } from '@fortawesome/free-solid-svg-icons';
import MobileLayout from '../../components/Mobile/MobileLayout';
import { ME, GET_MY_ADS, GET_MY_FAVORITES } from '../../graphql/queries';
import { UPDATE_PROFILE, UPDATE_PROFILE_PICTURE } from '../../graphql/mutations';
import { useAuth } from '../../context/AuthContext';

const MobileProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'saved'>('overview');

  const { data, refetch } = useQuery(ME);
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const [updateProfilePicture] = useMutation(UPDATE_PROFILE_PICTURE);

  // Fetch user's ads and favorites
  const { data: adsData } = useQuery(GET_MY_ADS, {
    variables: { status: null },
    fetchPolicy: 'cache-and-network',
  });

  const { data: favoritesData } = useQuery(GET_MY_FAVORITES, {
    fetchPolicy: 'cache-and-network',
  });

  const user = data?.me || authUser;
  const myAds = adsData?.myAds || [];
  const myFavorites = favoritesData?.myFavorites || [];

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    displayName: user?.displayName || '',
    bio: user?.bio || '',
    phone: user?.phone || '',
    city: user?.city || '',
    country: user?.country || '',
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          setLoading(true);
          await updateProfilePicture({
            variables: { image: reader.result }
          });
          refetch();
          setMessage('Profile picture updated!');
          setTimeout(() => setMessage(''), 3000);
        } catch (error) {
          setMessage('Failed to update profile picture');
          setTimeout(() => setMessage(''), 3000);
        } finally {
          setLoading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const result = await updateProfile({
        variables: { input: formData }
      });
      if (result.data?.updateProfile?.success) {
        setMessage('Profile updated successfully!');
        setIsEditing(false);
        refetch();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(result.data?.updateProfile?.message || 'Failed to update profile');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error: any) {
      setMessage(error.message || 'An error occurred');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: 'Ads', value: user?.activeListings || 0, icon: faTag, color: 'blue' },
    { label: 'Views', value: user?.totalViews || 0, icon: faEye, color: 'green' },
    { label: 'Favorites', value: user?.totalFavorites || 0, icon: faHeart, color: 'red' },
    { label: 'Rating', value: user?.averageRating?.toFixed(1) || 'N/A', icon: faStar, color: 'yellow' },
  ];

  const menuItems = [
    { icon: faBell, label: 'Notifications', action: () => navigate('/notifications'), color: 'blue' },
    { icon: faShieldAlt, label: 'Privacy & Security', action: () => navigate('/settings/privacy'), color: 'green' },
    { icon: faGlobe, label: 'Language', action: () => {}, color: 'purple' },
    { icon: faMoon, label: 'Dark Mode', action: () => {}, color: 'gray' },
  ];

  if (!user) {
    return (
      <MobileLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Header with Cover Image */}
      <div className="relative">
        {/* Cover Image */}
        <div className="h-40 bg-gradient-to-r from-blue-600 to-indigo-600">
          {user.coverImage && (
            <img
              src={user.coverImage}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Edit Button */}
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute top-4 right-4 px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full flex items-center gap-1.5 text-white text-sm font-medium"
          >
            <FontAwesomeIcon icon={faEdit} className="text-xs" />
            Edit
          </button>
        )}

        {/* Profile Picture */}
        <div className="absolute -bottom-16 left-4">
          <div className="relative">
            <div className="w-32 h-32 rounded-full border-4 border-white bg-gray-200 overflow-hidden shadow-lg">
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                  <FontAwesomeIcon icon={faUser} className="text-white text-4xl" />
                </div>
              )}
            </div>

            {/* Upload Button */}
            {isEditing && (
              <label className="absolute bottom-0 right-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-blue-600 transition-colors">
                <FontAwesomeIcon icon={faCamera} className="text-white text-sm" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={loading}
                />
              </label>
            )}

            {/* Verification Badge */}
            {user.isVerified && (
              <div className="absolute bottom-0 right-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center border-4 border-white">
                <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Info Section */}
      <div className="mt-20 px-4">
        {/* User Info */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User'}
          </h1>
          <p className="text-gray-600">@{user.username}</p>

          {user.bio && !isEditing && (
            <p className="text-gray-700 mt-3 text-sm leading-relaxed">{user.bio}</p>
          )}

          {user.userType && (
            <span className="inline-block mt-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {user.userType}
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl p-3 text-center shadow-sm"
            >
              <div className={`w-8 h-8 mx-auto mb-1 rounded-full flex items-center justify-center ${
                stat.color === 'blue' ? 'bg-blue-100' :
                stat.color === 'green' ? 'bg-green-100' :
                stat.color === 'red' ? 'bg-red-100' :
                'bg-yellow-100'
              }`}>
                <FontAwesomeIcon icon={stat.icon} className={`text-xs ${
                  stat.color === 'blue' ? 'text-blue-600' :
                  stat.color === 'green' ? 'text-green-600' :
                  stat.color === 'red' ? 'text-red-600' :
                  'text-yellow-600'
                }`} />
              </div>
              <p className="text-lg font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Edit Form */}
        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            {message && (
              <div className={`p-3 rounded-xl ${
                message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}>
                <p className="text-sm font-medium">{message}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={3}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-sm"
                placeholder="Tell us about yourself..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setMessage('');
                }}
                className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 bg-blue-500 text-white font-medium rounded-xl hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <>
            {/* Contact Info */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
              <h2 className="text-sm font-semibold text-gray-500 px-4 pt-4 pb-2">CONTACT INFORMATION</h2>

              {user.email && (
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 text-sm" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                </div>
              )}

              {user.phone && (
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faPhone} className="text-green-600 text-sm" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm text-gray-900">{user.phone}</p>
                  </div>
                </div>
              )}

              {(user.city || user.country) && (
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-purple-600 text-sm" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">Location</p>
                    <p className="text-sm text-gray-900">
                      {[user.city, user.country].filter(Boolean).join(', ') || 'Not set'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Account Info */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
              <h2 className="text-sm font-semibold text-gray-500 px-4 pt-4 pb-2">ACCOUNT INFO</h2>

              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faCalendar} className="text-yellow-600 text-sm" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Member Since</p>
                  <p className="text-sm text-gray-900">
                    {user.dateJoined ? new Date(user.dateJoined).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 px-4 py-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faChartLine} className="text-orange-600 text-sm" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="text-sm text-gray-900">
                    {user.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Settings */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4">
              <h2 className="text-sm font-semibold text-gray-500 px-4 pt-4 pb-2">QUICK SETTINGS</h2>

              {menuItems.map((item, index) => (
                <button
                  key={item.label}
                  onClick={item.action}
                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    item.color === 'blue' ? 'bg-blue-100' :
                    item.color === 'green' ? 'bg-green-100' :
                    item.color === 'purple' ? 'bg-purple-100' :
                    'bg-gray-100'
                  }`}>
                    <FontAwesomeIcon icon={item.icon} className={`text-sm ${
                      item.color === 'blue' ? 'text-blue-600' :
                      item.color === 'green' ? 'text-green-600' :
                      item.color === 'purple' ? 'text-purple-600' :
                      'text-gray-600'
                    }`} />
                  </div>
                  <span className="flex-1 text-left text-sm font-medium text-gray-900">{item.label}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-xs" />
                </button>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 px-1">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                  activeTab === 'overview'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                  activeTab === 'analytics'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                Analytics
              </button>
              <button
                onClick={() => setActiveTab('saved')}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium text-sm transition-all ${
                  activeTab === 'saved'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                Saved ({myFavorites.length})
              </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 px-1">MY ADS</h3>
                {myAds.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                    <FontAwesomeIcon icon={faTag} className="text-gray-400 text-3xl mb-3" />
                    <p className="text-gray-600 text-sm mb-3">No ads yet</p>
                    <Link
                      to="/post-ad"
                      className="inline-block px-6 py-2.5 bg-blue-500 text-white rounded-xl text-sm font-medium"
                    >
                      Post Ad
                    </Link>
                  </div>
                ) : (
                  myAds.slice(0, 5).map((ad: any, index: number) => (
                    <motion.div
                      key={ad.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={`/ad/${ad.slug}`}
                        className="bg-white rounded-2xl shadow-sm overflow-hidden block"
                      >
                        <div className="flex gap-3 p-3">
                          {ad.imagesList && ad.imagesList.length > 0 ? (
                            <img
                              src={ad.imagesList.find((img: any) => img.isPrimary)?.url || ad.imagesList[0].url}
                              alt={ad.title}
                              className="w-20 h-20 object-cover rounded-xl"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center">
                              <FontAwesomeIcon icon={faTag} className="text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">{ad.title}</h4>
                            <p className="text-blue-600 font-bold text-sm mb-1">
                              Rs. {Number(ad.price).toLocaleString()}
                            </p>
                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <FontAwesomeIcon icon={faEye} className="text-xs" />
                                {ad.viewCount || 0}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                ad.status === 'active' ? 'bg-green-100 text-green-700' :
                                ad.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                ad.status === 'sold' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {ad.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))
                )}
                {myAds.length > 5 && (
                  <Link
                    to="/my-ads"
                    className="block text-center py-3 text-blue-500 text-sm font-medium"
                  >
                    View all {myAds.length} ads →
                  </Link>
                )}
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 px-1">PERFORMANCE</h3>

                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center">
                    <FontAwesomeIcon icon={faEye} className="text-blue-600 text-lg mb-1" />
                    <p className="text-xl font-bold text-gray-900">
                      {myAds.reduce((sum: number, ad: any) => sum + (ad.viewCount || 0), 0)}
                    </p>
                    <p className="text-xs text-blue-600">Views</p>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3 text-center">
                    <FontAwesomeIcon icon={faHeart} className="text-red-600 text-lg mb-1" />
                    <p className="text-xl font-bold text-gray-900">
                      {myAds.reduce((sum: number, ad: any) => sum + (ad.favoriteCount || 0), 0)}
                    </p>
                    <p className="text-xs text-red-600">Favorites</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 text-center">
                    <FontAwesomeIcon icon={faChartLine} className="text-green-600 text-lg mb-1" />
                    <p className="text-xl font-bold text-gray-900">
                      {myAds.filter((ad: any) => ad.status === 'active').length}
                    </p>
                    <p className="text-xs text-green-600">Active</p>
                  </div>
                </div>

                {/* Per-Ad Analytics */}
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <h4 className="text-sm font-semibold text-gray-900">Ad Performance</h4>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {myAds.map((ad: any) => (
                      <div key={ad.id} className="px-4 py-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/ad/${ad.slug}`}
                            className="font-medium text-gray-900 text-sm line-clamp-1 hover:text-blue-600"
                          >
                            {ad.title}
                          </Link>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faEye} className="text-xs" />
                            {ad.viewCount || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faHeart} className="text-xs" />
                            {ad.favoriteCount || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Saved Ads Tab */}
            {activeTab === 'saved' && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-500 px-1">SAVED ADS</h3>
                {myFavorites.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
                    <FontAwesomeIcon icon={faBookmark} className="text-gray-400 text-3xl mb-3" />
                    <p className="text-gray-600 text-sm mb-3">No saved ads yet</p>
                    <Link
                      to="/"
                      className="inline-block px-6 py-2.5 bg-blue-500 text-white rounded-xl text-sm font-medium"
                    >
                      Browse Ads
                    </Link>
                  </div>
                ) : (
                  myFavorites.map((ad: any, index: number) => (
                    <motion.div
                      key={ad.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={`/ad/${ad.slug}`}
                        className="bg-white rounded-2xl shadow-sm overflow-hidden block"
                      >
                        <div className="flex gap-3 p-3">
                          {ad.primaryImage ? (
                            <img
                              src={ad.primaryImage}
                              alt={ad.title}
                              className="w-20 h-20 object-cover rounded-xl"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center">
                              <FontAwesomeIcon icon={faTag} className="text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">{ad.title}</h4>
                            <p className="text-blue-600 font-bold text-sm mb-1">
                              Rs. {Number(ad.price).toLocaleString()}
                            </p>
                            <div className="flex items-center text-xs text-gray-500">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1 text-xs" />
                              {ad.city || ad.location}
                            </div>
                          </div>
                          <button className="self-start p-2">
                            <FontAwesomeIcon icon={faHeart} className="text-red-500" />
                          </button>
                        </div>
                      </Link>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileProfile;
